import { DevConfig } from '../../config/environment/devConfig';
import { Platform, PlatformerLevel } from '../../shared/CharacterTypes';
import { SurfaceType, TileType } from '../../shared/TileEditorTypes';
import { gameLogger } from '../../utils/GameLogger';
import { SpriteTextureSystem } from '../systems/sprite/SpriteTextureSystem';
import { PlatformCache } from '../utils/level/PlatformCache';
import { TextureGenerator } from '../utils/rendering/TextureGenerator';
import { createNameTag } from '../rendering/utils/NameTag';
import { createPlatformVisual } from '../rendering/utils/PlatformVisual';
import { createGraphics } from '../rendering/utils/Graphics';
import { Z_LAYERS } from '../rendering/Z_LAYERS';
import { ServiceRegistry } from '../core/ServiceRegistry';
import type { IGraphics } from '../rendering/IRenderer';
import { createSprite } from '../rendering/utils/Sprite';
import type { INameTag, IPlatformVisual, ISprite } from '../rendering/IRenderer';
import { resolveScene } from '../effects/atmosphere/scene/resolveScene';
import { biomeHasTileProfile, resolveTileStyle, surfaceTypeToTileType } from '../rendering/textures/BiomeTextureModule';
import { bootSearchParams } from '../../config/boot/bootUrl';
import { createTerrainAccent } from './terrainAccent';

// Helper for conditional logging
const logMap = (msg: string, ...args: any[]) => { if (DevConfig.logging.map) gameLogger.debug('graphics', msg, { args }); };
const logTerrain = (msg: string, ...args: any[]) => { if (DevConfig.logging.terrain) gameLogger.debug('graphics', msg, { args }); };

/**
 * Classify a platform's main body into the TileType whose biome tint re-skins
 * it. Walls (the `wall_` id prefix used by bounding/collision walls, or `cover`
 * platforms) render as the collision `WALL` tile; every other standable
 * platform renders as the ground `FLOOR` tile. The biome's per-tile override
 * (e.g. pig_beach FLOOR → warm sand, coil_undercroft FLOOR → sickly black) then
 * decides the actual tint via `resolveTileStyle`.
 */
function platformBodyTileType(platform: Platform): TileType {
  if (platform.id?.startsWith('wall_') || platform.type === 'cover') return TileType.WALL;
  return TileType.FLOOR;
}

/**
 * Handles all visual rendering of map elements
 * Creates and manages platform and decoration visuals
 */
export class MapRenderer {
  private static instance: MapRenderer;
  private scene: unknown | null = null;
  private platformVisuals: IPlatformVisual[] = [];
  private platformLabels: INameTag[] = [];
  private decorationVisuals: ISprite[] = [];
  private terrainAccentVisuals: IGraphics[] = [];
  private decorationDataUrls: Map<string, string> = new Map();
  private platformCache: PlatformCache;


  private constructor() {
    this.platformCache = new PlatformCache(null);
  }

  public static getInstance(): MapRenderer {
    if (!MapRenderer.instance) {
      MapRenderer.instance = new MapRenderer();
    }
    return MapRenderer.instance;
  }

  /**
   * Set the Phaser scene reference for rendering
   */
  public setScene(scene: unknown): void {
    // CRITICAL FIX: Phaser reuses scene objects on restart but destroys all GameObjects.
    // We must ALWAYS clear cached visuals when setScene is called, not just when scene reference changes.
    const sceneKey = (scene as any)?.scene?.key || 'unknown';
    const oldSceneKey = this.scene ? (this.scene as any)?.scene?.key || 'unknown' : 'none';

    if (DevConfig.logging.terrain) {
      gameLogger.debug('graphics', `🌍 [TERRAIN] MapRenderer.setScene called: ${oldSceneKey} → ${sceneKey}`);
    }

    // Always clear cache - Phaser destroys GameObjects on scene restart even if scene ref is same
    logTerrain('🌍 [TERRAIN] Clearing platform cache (scene restart protection)');
    this.clearPlatformVisuals();

    this.scene = scene;
    this.platformCache.setScene(scene);
  }

  /**
   * Clear all cached platform visuals (e.g., on scene change)
   */
  public clearPlatformVisuals(): void {
    // Clear platforms (substrate-agnostic via IPlatformVisual)
    for (const p of this.platformVisuals) {
      try { p.destroy(); } catch { /* might be already destroyed */ }
    }
    this.platformVisuals = [];

    // Clear in-world platform labels
    for (const label of this.platformLabels) {
      try { label.destroy(); } catch { /* might be already destroyed */ }
    }
    this.platformLabels = [];

    // Clear decorations (substrate-aware ISprite handles)
    for (const d of this.decorationVisuals) {
      try { d.destroy(); } catch { /* might be already destroyed */ }
    }
    this.decorationVisuals = [];

    for (const accent of this.terrainAccentVisuals) {
      try { accent.destroy(); } catch { /* might be already destroyed */ }
    }
    this.terrainAccentVisuals = [];

    this.platformCache.clear();
  }

  /**
   * Render platform visuals in the scene
   * Creates visual rectangles for platforms with appropriate styling
   */
  public renderPlatforms(level: PlatformerLevel | any): IPlatformVisual[] {
    try {
      if (!this.scene) {
        gameLogger.warn('graphics', '[MapRenderer] Scene not set, cannot render platforms');
        return [];
      }

      // SAFETY CHECK: Handle malformed level data from API
      if (!level) {
        gameLogger.error('graphics', '❌ [MapRenderer] Level is null or undefined!');
        return [];
      }

      // Ensure platforms exists
      if (!level.platforms || !Array.isArray(level.platforms)) {
        gameLogger.error('graphics', '❌ [MapRenderer] level.platforms is missing or not an array!', { platforms: level?.platforms });
        return [];
      }

      const startTime = performance.now();
      logTerrain('🌍 [TERRAIN TRACE] Starting platform rendering...');
      logTerrain(`🌍 [TERRAIN TRACE] Level "${level.name || 'unknown'}" has ${level.platforms.length} platforms`);

      // Check if platforms are already cached
      const cachedCount = this.platformCache.size;
      if (cachedCount > 0) {
        logMap(`[MapRenderer] Using ${cachedCount} cached platforms`);
        return this.platformVisuals;
      }

      // Clear old platform visuals
      if (this.platformVisuals.length > 0) {
        logMap('[MapRenderer] Destroying old platform visuals');
        for (const p of this.platformVisuals) p.destroy();
        this.platformVisuals = [];
      }
      if (this.platformLabels.length > 0) {
        for (const label of this.platformLabels) label.destroy();
        this.platformLabels = [];
      }

      logTerrain('🌍 [TERRAIN TRACE] Creating platform GameObjects...');

      // Biome → tile-tint resolution (BiomeTextureModule wiring). Resolve the
      // active biome ONCE per level through the same precedence the atmosphere
      // layer uses (resolveScene: level.scene.biome → presetForLevel → null),
      // so the ground tiles can never desync from the mounted atmosphere
      // preset. When the biome has an authored tile profile, each platform's
      // flat fill is re-skinned with its biome tint; otherwise the platform's
      // authored `color` is kept (graceful degradation — never recolour maps
      // that lack a profile with the global default palette).
      const resolvedScene = resolveScene(level);
      const resolvedBiome = resolvedScene.biome;
      const biomeTintsTiles = biomeHasTileProfile(resolvedBiome);
      if (biomeTintsTiles) {
        logTerrain(`🌍 [TERRAIN] Biome "${resolvedBiome}" tile-tinting enabled — platforms re-skinned to match atmosphere`);
      }

      let platformCount = 0;

      // DEBUG: Draw outlines to see where platforms are (only if terrain tracing enabled).
      // Phase H10 Arc δ — substrate-agnostic via createGraphics.
      let debugGraphics: IGraphics | null = null;
      if (DevConfig.logging.terrain) {
        debugGraphics = createGraphics(this.scene, { depth: Z_LAYERS.TERRAIN_DEBUG_OUTLINES });
      }

      level.platforms.forEach((platformData: Platform, index: number) => {
        if (!this.scene) return;

        // Safety check for color
        let color = 0x808080; // Default gray
        if (platformData.color && typeof platformData.color === 'string' && platformData.color.startsWith('#')) {
          try {
            color = parseInt(platformData.color.slice(1), 16);
          } catch (_e) {
            gameLogger.warn('graphics', `⚠️ [TERRAIN] Failed to parse color "${platformData.color}" for platform ${index + 1}`);
          }
        }

        // Re-skin the platform body with its biome tile tint when the active
        // biome has a profile (v1 — flat-fill tint; textureKey sheets come
        // later). Teleporters/ropes are invisible as platforms so their tint
        // is irrelevant; skip the lookup for them.
        const isTeleporter = platformData.type === 'teleporter';
        const isRope = platformData.type === 'rope';
        const isDecorative = platformData.type === 'decorative';
        const useAuthoredColor = platformData.properties?.useAuthoredColor === true;
        // Decorative silhouettes are authored as palette accents (windows,
        // lanterns, tree hollows, ruin shadows). Re-tinting all of them to the
        // biome FLOOR/WALL swatch turns the landmark into one giant gray block.
        if (biomeTintsTiles && !isTeleporter && !isRope && !isDecorative && !useAuthoredColor) {
          color = resolveTileStyle(resolvedBiome, platformBodyTileType(platformData)).tint;
        }

        const platform = createPlatformVisual(this.scene, {
          x: platformData.x + platformData.width / 2,
          y: platformData.y + platformData.height / 2,
          width: platformData.width,
          height: platformData.height,
          color,
          depth: Z_LAYERS.PLATFORMS,
        });

        if (isTeleporter || isRope) {
          // Teleporters and ropes are invisible as platforms.
          // They have their own entity visuals (TeleporterEntity, RopeEntity).
          platform.setVisible(false);
        }

        logTerrain(`🧱 [TERRAIN] Created platform ${index + 1}: pos=(${platformData.x}, ${platformData.y}) size=${platformData.width}x${platformData.height} color=${platformData.color}`);

        // Platform collision is handled by custom CollisionResolver (AABB against Platform data).
        // No Phaser physics body needed — keeps rendering and physics cleanly separated.

        // Add to visual array
        this.platformVisuals.push(platform);
        this.platformCache.set(platformData.id || `platform_${index}`, platform);
        platformCount++;

        // Optional in-world label (ruler ticks, signs, debug markers).
        // Doesn't affect collision / projectile / LOS — those gates read
        // platformData.type. Decorative platforms also render with low
        // alpha so the label is the dominant visual.
        if (platformData.label) {
          const labelTag = createNameTag(
            this.scene,
            platformData.x + platformData.width / 2,
            platformData.y - 14, // float above the platform
            platformData.label,
            {
              depth: Z_LAYERS.PLATFORM_LABELS,
              fontFamily: 'monospace',
              fontSize: 12,
              color: 0xffffff,
              strokeWidth: 2,
              anchor: { x: 0.5, y: 1 },
            },
          );
          this.platformLabels.push(labelTag);
        }
        if (isDecorative) {
          platform.setAlpha(0.45);
        }

        const terrain = createTerrainAccent(this.scene, level.id, platformData, color, resolvedScene.groundY);
        if (terrain) {
          if (terrain.ownsBody) platform.setVisible(false);
          this.terrainAccentVisuals.push(terrain.accent);
        }

        // Secondary Surface Overlay Rendering (top 25% of tile)
        if (platformData.hasSurface && platformData.surfaceType !== undefined) {
          // Calculate surface height as 25% of platform height (typically tile height)
          const surfaceHeight = platformData.surfaceHeight || Math.floor(platformData.height * 0.25);

          if ((platformData.type === 'normal' || platformData.type === 'moving') && (!platformData.id || !platformData.id.startsWith('wall_'))) {
            try {
              // Color mapping for surface types
              const surfaceColors: Record<number, number> = {
                [SurfaceType.GRASS]: 0x58D68D,         // Vibrant green
                [SurfaceType.SAND]: 0xF4D03F,          // Sandy yellow
                [SurfaceType.SNOW]: 0xFFFFFF,          // White
                [SurfaceType.MOSS]: 0x7D8471,          // Gray-green moss
                [SurfaceType.WATER_SHALLOW]: 0x5DADE2, // Light blue (semi-transparent)
                [SurfaceType.LAVA_CRACKS]: 0xFF5733,   // Orange-red
                [SurfaceType.FLOWERS]: 0xFF69B4,       // Pink flowers
                [SurfaceType.VINES]: 0x228B22,         // Forest green vines
              };

              const surfaceColor = surfaceColors[platformData.surfaceType] || 0x58D68D;
              // Re-skin the surface band with its biome tile tint (GRASS
              // surface → biome GRASS tint, sandy → biome FLOOR tint, …) so
              // the top 25% tracks the atmosphere alongside the body. Falls
              // back to the vibrant default when the biome has no profile.
              const resolvedSurfaceColor = biomeTintsTiles
                ? resolveTileStyle(resolvedBiome, surfaceTypeToTileType(platformData.surfaceType)).tint
                : surfaceColor;
              const isLiquid = platformData.surfaceType === SurfaceType.WATER_SHALLOW || platformData.surfaceType === SurfaceType.LAVA_CRACKS;

              const surface = createPlatformVisual(this.scene, {
                x: platformData.x + platformData.width / 2,
                y: platformData.y, // Top edge — anchor here
                width: platformData.width + 4, // Slightly wider for overhang
                height: surfaceHeight,
                color: resolvedSurfaceColor,
                depth: Z_LAYERS.PLATFORM_SURFACES,
                originX: 0.5,
                originY: 0,
                alpha: isLiquid ? 0.6 : 1,
              });

              this.platformVisuals.push(surface); // TRACK FOR CLEANUP
              logTerrain(`   ↳ [TERRAIN] Surface overlay added: ${platformData.surfaceType}`);
            } catch (e) {
              gameLogger.error('graphics', `❌ [TERRAIN] Failed to add surface for platform ${index + 1}`, { error: e });
            }
          }
        }

        // Special visual effects for different platform types
        if (platformData.type === 'bouncy') {
          platform.setStroke(0xFFFFFF, 2);
        } else if (platformData.type === 'moving') {
          platform.setStroke(0x00FFFF, 2);
        } else if (platformData.isOneWay && DevConfig.logging.terrain) {
          // One-way collision outlines are diagnostic information. Showing
          // them in the default client made authored ledges read as green
          // wireframe boxes.
          platform.setStroke(0x90EE90, 1);
        }

        // Hide bounding walls
        if (platformData.id && platformData.id.startsWith('wall_')) {
          platform.setVisible(false);
          logTerrain(`   ↳ [TERRAIN] Wall hidden: ${platformData.id}`);
        }

        // DEBUG: Draw red outline around platform bounds.
        if (debugGraphics) {
          debugGraphics.strokeRect(
            platformData.x,
            platformData.y,
            platformData.width,
            platformData.height,
            0xff0000,
            2,
            1,
          );
        }
      });

      const endTime = performance.now();
      logTerrain(`✅ [MapRenderer] Rendered ${platformCount} platforms in ${(endTime - startTime).toFixed(2)}ms`);

      // TERRAIN TRACE: Final verification
      if (DevConfig.logging.terrain) {
        const cam = ServiceRegistry.getInstance()
          .get('CameraSystem', { optional: true })
          ?.getManager()
          ?.getICamera();
        gameLogger.debug('graphics', '=== TERRAIN VERIFICATION ===', {
          context: (this.scene as any)?.scene?.key,
          childrenCount: (this.scene as any).children?.length,
          camera: cam
            ? { scrollX: cam.scrollX, scrollY: cam.scrollY, zoom: cam.zoom, width: cam.width, height: cam.height }
            : { error: 'CameraSystem not registered' },
          visualsCount: this.platformVisuals.length
        });
      }

      return this.platformVisuals;
    } catch (error) {
      gameLogger.error('graphics', '❌ [MapRenderer] Critical error in renderPlatforms:', { error });
      return [];
    }
  }

  /**
   * Render decoration visuals in the scene.
   * Phase H — substrate-aware via createSprite. Each decoration ISprite
   * loads its texture from a generated SVG dataUrl (Phaser side caches
   * via SpriteTextureSystem; Pixi side caches via Pixi Assets.load).
   * The dataUrls are memoized in decorationDataUrls so subsequent calls
   * skip the SVG-generation cost.
   */
  public renderDecorations(level: PlatformerLevel): ISprite[] {
    if (!this.scene) {
      gameLogger.warn('graphics', '[MapRenderer] Scene not set, cannot render decorations');
      return [];
    }

    const startTime = performance.now();
    gameLogger.debug('graphics', '🌍 [MapRenderer] Starting decoration rendering...');

    const decorations: ISprite[] = [];

    // Pre-generate dataUrls (memoized) + pre-load into Phaser texture cache.
    // Pixi side loads on first sprite create via loadTexture call.
    const textureSystem = SpriteTextureSystem.getInstance(this.scene);
    const decorationTextures = [
      { key: 'palm_tree', generator: TextureGenerator.generatePalmTreeSVG, type: 'prop' as const },
      { key: 'shell', generator: TextureGenerator.generateShellSVG, type: 'prop' as const },
      { key: 'starfish', generator: TextureGenerator.generateStarfishSVG, type: 'prop' as const }
    ];

    const texturePromises = decorationTextures.map(({ key, generator, type }) => {
      if (!this.decorationDataUrls.has(key)) {
        this.decorationDataUrls.set(key, generator());
      }
      // Phase H10 Arc λ.11 hotfix (2026-05-10): guard `scene.textures`
      // (Phaser texture manager) — undefined post-Stage-D-2. Pre-fix this
      // threw inside the .map callback, killing GameScene.create() right
      // between renderPlatforms and handlePlayerSpawn → no character.
      // SpriteTextureSystem maintains its own cache; loadTexture is
      // idempotent (early-returns if already loaded), so skipping the
      // pre-check just means we issue a redundant loadTexture call that
      // no-ops on the second pass.
      const sceneTextures = (this.scene as unknown as { textures?: { exists?: (k: string) => boolean } })?.textures;
      if (sceneTextures?.exists?.(key)) {
        return Promise.resolve();
      }
      return textureSystem.loadTexture({
        key,
        type,
        dataUrl: this.decorationDataUrls.get(key)!,
      }).then(() => undefined);
    });

    Promise.all(texturePromises).then(() => {
      // CRASH FIX: If the scene was destroyed or swapped while textures were downloading asynchronously,
      // this.scene or this.scene.add will be null, and we must abort rendering.
      if (!this.scene || !(this.scene as any).sys || !(this.scene as any).add) {
        logMap('⚠️ [MapRenderer] Scene destroyed during texture load. Aborting decoration render.');
        return;
      }

      level.platforms.forEach((platform: Platform) => {
        if (platform.type === 'normal') {
          if (level.id === 'starter-map') {
            return;
          } else if (level.id === 'pig_beach') {
            this.createBeachDecorations(platform, decorations);
          } else if (!resolveScene(level).biome) {
            // The legacy fallback only knows beach shells and starfish. Do
            // not scatter those across authored biome maps; NatureLayer owns
            // their biome-correct scenery.
            this.createDefaultDecorations(platform, decorations);
          }
        }
      });

      // Track all created decorations for cleanup
      decorations.forEach(d => this.decorationVisuals.push(d));
    });

    const endTime = performance.now();
    gameLogger.debug('graphics', `✅ [MapRenderer] Rendered decorations in ${(endTime - startTime).toFixed(2)}ms`);

    return decorations;
  }

  /**
   * Spawn one decoration sprite with the given texture key. Under Phaser
   * substrate the texture is pre-loaded in renderDecorations; under
   * Pixi mode the sprite starts with EMPTY texture and async-loads via
   * loadTexture (alpha gates from 0 → 1 on resolution).
   */
  private spawnDecorationSprite(
    key: string,
    x: number, y: number,
    width: number, height: number,
    depth: number,
    originX: number, originY: number,
    scale: number,
    flipX = false,
    angle = 0,
  ): ISprite {
    // Provisional alpha: 1 if texture is already cached on this substrate
    // (Phaser path always has it pre-loaded; Pixi path may not yet),
    // otherwise 0 to hide the EMPTY-texture sprite during async load.
    const sprite = createSprite(this.scene!, {
      x, y,
      initialTextureKey: key,
      width, height,
      depth,
      alpha: 0,
    });
    sprite.setOrigin(originX, originY);
    sprite.setScale(scale);
    if (flipX) sprite.setFlipX(true);
    if (angle !== 0) sprite.setAngle(angle);

    // If texture cached, apply + reveal immediately. Otherwise async-load,
    // then apply + reveal on resolution.
    if (sprite.hasTexture(key)) {
      sprite.setTexture(key);
      sprite.setAlpha(1);
    } else {
      const dataUrl = this.decorationDataUrls.get(key);
      if (dataUrl) {
        sprite.loadTexture(key, dataUrl).then(() => {
          if (!sprite.isActive()) return;
          sprite.setTexture(key);
          sprite.setAlpha(1);
        }).catch(err => {
          gameLogger.warn('graphics', `[MapRenderer] Failed to load decoration ${key}:`, err);
          if (sprite.isActive()) sprite.setAlpha(1);
        });
      }
    }
    return sprite;
  }

  /**
   * Create beach-themed decorations (substrate-aware via spawnDecorationSprite).
   * Width/height in createSprite are placeholder display sizes (the SVG
   * texture's natural pixel dimensions take over once loadTexture
   * applies the texture; setScale handles the size variation).
   */
  private createBeachDecorations(platform: Platform, decorations: ISprite[]): void {
    const numDecor = Math.min(Math.floor(platform.width / 120), 5); // Max 5 per platform

    for (let i = 0; i < numDecor; i++) {
      const margin = 30;
      const x = platform.x + margin + Math.random() * (platform.width - margin * 2);
      const y = platform.y;

      const rand = Math.random();

      if (rand > 0.7) {
        // Palm Tree (background layer, originY=1 → bottom-anchored)
        const scale = 0.8 + Math.random() * 0.6;
        const flipX = Math.random() > 0.5;
        decorations.push(this.spawnDecorationSprite('palm_tree', x, y, 60, 80, 0.1, 0.5, 1, scale, flipX, 0));
      } else if (rand > 0.4) {
        // Shell (on sand)
        const scale = 0.4 + Math.random() * 0.6;
        decorations.push(this.spawnDecorationSprite('shell', x, y - 8, 32, 32, 0.8, 0.5, 1, scale, false, Math.random() * 180 - 90));
      } else if (rand > 0.2) {
        // Starfish (on sand)
        const scale = 0.6 + Math.random() * 0.8;
        decorations.push(this.spawnDecorationSprite('starfish', x, y - 5, 32, 32, 0.9, 0.5, 1, scale, false, Math.random() * 360));
      }
      // 20% chance of nothing (clean sand)
    }
  }

  /**
   * Create default decorations for other maps.
   */
  private createDefaultDecorations(platform: Platform, decorations: ISprite[]): void {
    const numDecor = Math.min(Math.floor(platform.width / 100), 3);

    for (let i = 0; i < numDecor; i++) {
      const x = platform.x + Math.random() * (platform.width - 20) + 10;
      const y = platform.y - Math.random() * 10;

      const rand = Math.random();
      if (rand > 0.6) {
        const decorType = Math.random() > 0.5 ? 'shell' : 'starfish';
        const scale = 0.3 + Math.random() * 0.4;
        // Default-decoration origin is (0.5, 0.5) (Phaser image default)
        decorations.push(this.spawnDecorationSprite(decorType, x, y, 32, 32, 0.5, 0.5, 0.5, scale, false, 0));
      }
    }
  }


  /**
   * Render testing-map label overlays as Phaser text at (x, y).
   *
   * Load-bearing for visual QA: labels burn into capture PNGs so
   * reviewers don't need meta.json to know what the frame is meant
   * to show. Gated — only runs when either `?labels=1` URL param
   * OR `globalThis.__vqaLabels === true` is set. Gameplay stays
   * uncluttered otherwise.
   *
   * Source of label data: any testing-map loaded via `loader.ts`
   * carries `vqaLabels?: { text, x, y, color? }[]` on the level
   * object. This method is a no-op for gameplay maps (no field,
   * returns early).
   */
  public renderVqaLabels(level: PlatformerLevel & { vqaLabels?: Array<{ text: string; x: number; y: number; color?: string }> }): INameTag[] {
    if (!this.scene) return [];
    const labels = level.vqaLabels;
    if (!labels || labels.length === 0) return [];

    // Gate: URL `?labels=1` or global flag (set by test harness env).
    let enabled = false;
    try {
      enabled = bootSearchParams().get('labels') === '1' || (globalThis as any).__vqaLabels === true;
    } catch { /* no-op */ }
    if (!enabled) return [];

    // Substrate-aware via createNameTag (Stage 5w migration). Color is
    // a CSS hex string in the source data; coerce to int for NameTagStyle.
    const texts: INameTag[] = [];
    for (const label of labels) {
      const colorInt = label.color
        ? parseInt(label.color.replace('#', ''), 16) || 0xffffff
        : 0xffffff;
      const t = createNameTag(this.scene, label.x, label.y, label.text, {
        depth: 20, // Above platforms/entities, below HUD.
        fontFamily: 'monospace',
        fontSize: 13,
        color: colorInt,
        strokeWidth: 3,
      });
      texts.push(t);
    }
    gameLogger.info('graphics', `[MapRenderer] Rendered ${texts.length} VQA label(s)`);
    return texts;
  }

  /**
   * Clear all rendered visuals
   */
  public clearVisuals(): void {
    if (this.platformVisuals.length > 0) {
      for (const p of this.platformVisuals) p.destroy();
      this.platformVisuals = [];
    }
    if (this.platformLabels.length > 0) {
      for (const label of this.platformLabels) label.destroy();
      this.platformLabels = [];
    }
    for (const accent of this.terrainAccentVisuals) {
      try { accent.destroy(); } catch { /* gone */ }
    }
    this.terrainAccentVisuals = [];
    gameLogger.info('graphics', '[MapRenderer] All visuals cleared');
  }

  /**
   * Get platform visuals
   */
  /**
   * Hide a platform's visual by its authored id (e.g. the GM-race start
   * gate opening at GO). Visual-only — dropping the collision is the
   * physics system's job (PhysicsSystem.setPlatforms with the platform
   * filtered out). The visual is recreated on the next map render, so a
   * fresh course load starts with the gate closed again.
   */
  public hidePlatformVisualById(platformId: string): boolean {
    const visual = this.platformCache.get(platformId);
    if (!visual) return false;
    visual.setVisible(false);
    return true;
  }

  public getPlatformVisuals(): IPlatformVisual[] {
    return this.platformVisuals;
  }

  /**
   * Update platform visuals (for moving platforms)
   */
  public updatePlatformVisuals(level: PlatformerLevel): void {
    if (!this.scene) return;

    let updatedCount = 0;
    // Only update positions of moving platforms, don't re-render everything
    level.platforms.forEach((platformData: Platform) => {
      if (platformData.type === 'moving') {
        const visual = this.platformCache.get(platformData.id || '');
        if (visual && visual.setPosition) {
          visual.setPosition(
            platformData.x + platformData.width / 2,
            platformData.y + platformData.height / 2
          );
          updatedCount++;
        }
      }
    });


    if (updatedCount > 0) {
      gameLogger.debug('graphics', `[MapRenderer] Updated ${updatedCount} moving platform positions`);
    }
  }
}
